import { X, ExternalLink, FileText, Calendar, User, RefreshCw } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { formatDateAU } from '@/lib/utils';

export default function DocumentPreviewModal({ document, type, onClose }) {
  if (!document) return null;

  const isGovernance = type === 'governance';
  
  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 animate-fade-in" data-testid="document-preview-modal">
      <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full mx-4 max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-[#E8E4DF]">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-[#4A3B32]/10 flex items-center justify-center">
              <FileText className="w-5 h-5 text-[#4A3B32]" />
            </div>
            <div>
              <h2 className="font-heading text-lg text-[#2C2420]">{document.title}</h2>
              <p className="text-sm text-[#5D524D]">
                {isGovernance ? document.category : document.type}
              </p>
            </div>
          </div>
          <Button 
            variant="ghost" 
            size="sm" 
            onClick={onClose}
            className="hover:bg-[#F0EBE6]"
            data-testid="close-modal-btn"
          >
            <X className="w-5 h-5" />
          </Button>
        </div>

        {/* Content */}
        <div className="p-6 space-y-6">
          {/* Document Preview Placeholder */}
          <div className="bg-[#F9F7F5] rounded-lg p-8 border border-dashed border-[#D4CEC8] text-center">
            <FileText className="w-16 h-16 text-[#8C8078] mx-auto mb-4" />
            <p className="text-[#5D524D] font-medium">Document Preview</p>
            <p className="text-sm text-[#8C8078] mt-1">
              This is a demo preview. In production, the actual document would be rendered here.
            </p>
            <p className="text-xs text-[#8C8078] mt-4">
              Source: {document.sourceSystem}
            </p>
          </div>

          {/* Metadata Grid */}
          <div className="grid grid-cols-2 gap-4">
            {isGovernance ? (
              <>
                <div className="p-3 bg-[#F9F7F5] rounded-lg">
                  <div className="flex items-center gap-2 text-[#8C8078] text-xs mb-1">
                    <Calendar className="w-3.5 h-3.5" />
                    Last Updated
                  </div>
                  <p className="text-sm font-medium text-[#2C2420]">
                    {formatDateAU(document.lastUpdated)}
                  </p>
                </div>
                <div className="p-3 bg-[#F9F7F5] rounded-lg">
                  <div className="flex items-center gap-2 text-[#8C8078] text-xs mb-1">
                    <User className="w-3.5 h-3.5" />
                    Owner
                  </div>
                  <p className="text-sm font-medium text-[#2C2420]">
                    {document.owner}
                  </p>
                </div>
              </>
            ) : (
              <>
                <div className="p-3 bg-[#F9F7F5] rounded-lg">
                  <div className="flex items-center gap-2 text-[#8C8078] text-xs mb-1">
                    <User className="w-3.5 h-3.5" />
                    Adviser
                  </div>
                  <p className="text-sm font-medium text-[#2C2420]">
                    {document.adviser}
                  </p>
                </div>
                <div className="p-3 bg-[#F9F7F5] rounded-lg">
                  <div className="flex items-center gap-2 text-[#8C8078] text-xs mb-1">
                    <Calendar className="w-3.5 h-3.5" />
                    Date Issued
                  </div>
                  <p className="text-sm font-medium text-[#2C2420]">
                    {formatDateAU(document.date)}
                  </p>
                </div>
                {document.reviewDue && (
                  <div className="p-3 bg-[#F9F7F5] rounded-lg col-span-2">
                    <div className="flex items-center gap-2 text-[#8C8078] text-xs mb-1">
                      <RefreshCw className="w-3.5 h-3.5" />
                      Review Due
                    </div>
                    <p className="text-sm font-medium text-[#2C2420]">
                      {formatDateAU(document.reviewDue)}
                    </p>
                  </div>
                )}
              </>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between p-4 border-t border-[#E8E4DF] bg-[#F9F7F5]">
          <p className="text-xs text-[#8C8078]">
            Indexed from {document.sourceSystem}
          </p>
          <div className="flex items-center gap-2">
            <Button variant="outline" onClick={onClose} className="caffeine-btn-secondary">
              Close
            </Button>
            <Button className="caffeine-btn">
              <ExternalLink className="w-4 h-4 mr-2" />
              Open in Source
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
